<!-- eslint-disable @intlify/vue-i18n/no-dynamic-keys -->
<script setup lang="ts">
import type { EmbedSettingsControls } from "@/composables/useEmbedSettings";
import { useProductNoun } from "@/composition/useProductNoun";
import { EMBED_LAYOUT_TYPES, type EmbedLayoutId } from "@/util/embedCode";
import ColorInput from "@/components/common/Elements/ColorInput.vue";
import IconPicker from "@/components/common/Elements/IconPicker.vue";
import {
  Link01Icon,
  LinkBroken01Icon,
} from "@gohighlevel/ghl-icons/24/outline";
import {
  UIButton,
  UIInput,
  UIInputNumber,
  UISelect,
  UISlider,
  UISwitch,
} from "@gohighlevel/ghl-ui";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import EmbedTypeThumbnail from "./EmbedTypeThumbnail.vue";

const props = defineProps<{ controls: EmbedSettingsControls }>();
const { t } = useI18n();
const { assetParams } = useProductNoun();
const linkedCorners = ref(true);
const types = computed<
  Array<{ id: EmbedLayoutId; key: string; label: string }>
>(() =>
  EMBED_LAYOUT_TYPES.map(({ id, key, labelKey }) => ({
    id,
    key,
    label: t(labelKey),
  }))
);
const hasLauncherToggle = computed(() =>
  ["STICKY_SIDEBAR", "POLITE_SLIDE_IN"].includes(
    props.controls.settings.layout.id
  )
);
const hasPosition = hasLauncherToggle;
const hasButtonFields = computed(
  () =>
    props.controls.settings.layout.id === "POPUP" ||
    (hasLauncherToggle.value && props.controls.settings.layout.allowMinimize)
);
const buttonTypeOptions = computed(() => [
  { label: t("integrate.embed.text"), value: "text" },
  { label: t("integrate.embed.iconAndText"), value: "textIcon" },
  { label: t("integrate.embed.iconOnly"), value: "icon" },
]);
const buttonSizeOptions = computed(() => [
  { label: t("integrate.embed.small"), value: "small" },
  { label: t("integrate.embed.medium"), value: "medium" },
  { label: t("integrate.embed.large"), value: "large" },
]);
const corners = computed(() => [
  { id: "tl" as const, label: t("integrate.embed.corner.tl"), rotation: -90 },
  { id: "tr" as const, label: t("integrate.embed.corner.tr"), rotation: 0 },
  { id: "bl" as const, label: t("integrate.embed.corner.bl"), rotation: 180 },
  { id: "br" as const, label: t("integrate.embed.corner.br"), rotation: 90 },
]);
const setButtonColor = (value: string) =>
  props.controls.setLayoutText("buttonColor", `#${value.replace(/^#/, "")}`);
const setCorner = (corner: "tl" | "tr" | "bl" | "br", value: number) => {
  if (linkedCorners.value) {
    props.controls.setNumericLayoutValue("cornerRadius", value);
    return;
  }
  props.controls.setCornerValue(corner, value);
};
</script>

<template>
  <section data-testid="embed-general-settings" class="embed-settings">
    <h3 class="embed-settings__field-label">
      {{ t("integrate.embed.chooseType") }}
    </h3>
    <div
      class="embed-settings__types"
      role="radiogroup"
      :aria-label="t('integrate.embed.chooseType')"
    >
      <UIButton
        v-for="type in types"
        :key="type.id"
        :id="`embed-type-${type.key}`"
        :data-testid="`embed-type-${type.key}`"
        class="embed-settings__type"
        :class="{
          'embed-settings__type--active':
            controls.settings.layout.id === type.id,
        }"
        text
        role="radio"
        :aria-checked="controls.settings.layout.id === type.id"
        @click="controls.selectLayout(type.id)"
      >
        <EmbedTypeThumbnail
          :type="type.key"
          :selected="controls.settings.layout.id === type.id"
        />
        <span class="embed-settings__type-label">{{ type.label }}</span>
      </UIButton>
    </div>
    <div
      v-if="hasLauncherToggle"
      data-testid="embed-launcher-toggle"
      class="embed-settings__row"
    >
      <span class="embed-settings__launcher-copy">
        <span
          data-testid="embed-launcher-label"
          class="embed-settings__field-label"
        >
          {{ t("integrate.embed.showLauncher", assetParams) }}
        </span>
        <span
          data-testid="embed-launcher-help"
          class="embed-settings__field-help"
        >
          {{ t("integrate.embed.showLauncherHelp", assetParams) }}
        </span>
      </span>
      <UISwitch
        :value="controls.settings.layout.allowMinimize"
        :aria-label="t('integrate.embed.showLauncher', assetParams)"
        @update:value="controls.setAllowMinimize($event)"
      />
    </div>
    <div
      v-if="hasPosition"
      data-testid="embed-position"
      class="embed-settings__position"
    >
      <span>{{ t("integrate.embed.position") }}</span>
      <div
        data-testid="embed-position-options"
        class="embed-settings__position-options"
        role="radiogroup"
        :aria-label="t('integrate.embed.position')"
      >
        <UIButton
          id="embed-position-left"
          class="embed-settings__position-button"
          :class="{
            'embed-settings__position-button--active':
              controls.settings.layout.isLeftAligned,
          }"
          role="radio"
          :aria-checked="controls.settings.layout.isLeftAligned"
          :aria-label="t('integrate.embed.left')"
          @click="controls.setPosition('left')"
        >
          <span
            class="embed-settings__position-glyph embed-settings__position-glyph--left"
          />
        </UIButton>
        <UIButton
          id="embed-position-right"
          class="embed-settings__position-button"
          :class="{
            'embed-settings__position-button--active':
              controls.settings.layout.isRightAligned,
          }"
          role="radio"
          :aria-checked="controls.settings.layout.isRightAligned"
          :aria-label="t('integrate.embed.right')"
          @click="controls.setPosition('right')"
        >
          <span
            class="embed-settings__position-glyph embed-settings__position-glyph--right"
          />
        </UIButton>
      </div>
    </div>
    <div data-testid="embed-dimensions" class="embed-settings__dimensions">
      <label for="integrate-form-width">
        <span>{{ t("integrate.embed.formWidth") }}</span>
        <span class="embed-settings__number-with-unit">
          <UIInputNumber
            id="integrate-form-width"
            :model-value="controls.settings.layout.formWidth"
            :placeholder="String(controls.defaultFormWidth.value)"
            :show-button="false"
            @update:model-value="
              controls.setNumericLayoutValue('formWidth', $event)
            "
            @blur="controls.commitDimension('formWidth')"
          >
            <template #suffix>
              <span
                data-testid="embed-dimension-suffix"
                class="embed-settings__unit"
                >{{ t("common.px_uppercase") }}</span
              >
            </template>
          </UIInputNumber>
        </span>
      </label>
      <label for="integrate-form-height">
        <span>{{ t("integrate.embed.formHeight") }}</span>
        <span class="embed-settings__number-with-unit">
          <UIInputNumber
            id="integrate-form-height"
            :model-value="controls.settings.layout.formHeight"
            :show-button="false"
            @update:model-value="
              controls.setNumericLayoutValue('formHeight', $event)
            "
            @blur="controls.commitDimension('formHeight')"
          >
            <template #suffix>
              <span
                data-testid="embed-dimension-suffix"
                class="embed-settings__unit"
                >{{ t("common.px_uppercase") }}</span
              >
            </template>
          </UIInputNumber>
        </span>
      </label>
    </div>
    <div
      v-if="hasButtonFields"
      data-testid="embed-button-fields"
      class="embed-settings__fields"
    >
      <label for="integrate-button-label">{{
        t("integrate.embed.buttonLabel")
      }}</label
      ><UIInput
        id="integrate-button-label"
        :model-value="controls.settings.layout.minimizedTitle"
        @update:model-value="controls.setLayoutText('minimizedTitle', $event)"
      />
      <ColorInput
        :label="t('integrate.embed.buttonColor')"
        typography="UITextSmMedium"
        :color="
          (controls.settings.layout.buttonColor || '101828').replace(/^#/, '')
        "
        @update:color="setButtonColor"
      />
      <div class="embed-settings__two-column">
        <div>
          <label for="integrate-button-type">{{
            t("integrate.embed.buttonType")
          }}</label>
          <UISelect
            id="integrate-button-type"
            :value="controls.settings.layout.buttonType"
            :options="buttonTypeOptions"
            @update:value="controls.setLayoutSelect('buttonType', $event)"
          />
        </div>
        <div>
          <label for="integrate-button-size">{{
            t("integrate.embed.buttonSize")
          }}</label>
          <UISelect
            id="integrate-button-size"
            :value="controls.settings.layout.buttonSize"
            :options="buttonSizeOptions"
            @update:value="controls.setLayoutSelect('buttonSize', $event)"
          />
        </div>
      </div>
      <div
        v-if="(controls.settings.layout.buttonType ?? 'text') !== 'text'"
        class="embed-settings__picker-field"
      >
        <span
          data-testid="embed-icon-label"
          class="embed-settings__picker-label"
          >{{ t("integrate.embed.icon") }}</span
        >
        <IconPicker
          id="embed-icon-picker"
          class="embed-settings__icon-picker"
          :model-value="controls.settings.layout.buttonIcon || 'chat'"
          label=""
          @update:model-value="controls.setLayoutText('buttonIcon', $event)"
        />
      </div>
      <fieldset class="embed-settings__corners">
        <legend>{{ t("integrate.embed.cornerRadius") }}</legend>
        <label
          v-for="corner in corners"
          :key="corner.id"
          :for="`integrate-corner-${corner.id}`"
          class="embed-settings__corner"
          :class="`embed-settings__corner--${corner.id}`"
        >
          <span class="sr-only">{{ corner.label }}</span>
          <UIInputNumber
            :id="`integrate-corner-${corner.id}`"
            :model-value="
              controls.settings.layout.cornerRadius?.[corner.id] || 0
            "
            :show-button="false"
            :max="50"
            :aria-label="corner.label"
            @update:model-value="setCorner(corner.id, $event)"
            @blur="controls.commitCornerValue(corner.id, linkedCorners)"
          >
            <template #prefix>
              <span
                data-testid="embed-corner-glyph"
                class="material-symbols-outlined embed-settings__corner-glyph"
                :class="`embed-settings__corner-glyph--${corner.id}`"
                :style="{ transform: `rotate(${corner.rotation}deg)` }"
                aria-hidden="true"
              ></span>
            </template>
            <template #suffix>
              <span
                data-testid="embed-corner-suffix"
                class="embed-settings__unit"
                >{{ t("common.px_uppercase") }}</span
              >
            </template>
          </UIInputNumber>
        </label>
        <UIButton
          id="embed-corner-link"
          data-testid="embed-corner-link"
          class="embed-settings__corner-link"
          :aria-label="t('integrate.embed.linkCornerRadius')"
          :aria-pressed="linkedCorners"
          @click="linkedCorners = !linkedCorners"
        >
          <Link01Icon v-if="linkedCorners" class="h-4 w-4" />
          <LinkBroken01Icon v-else class="h-4 w-4" />
        </UIButton>
      </fieldset>
      <label for="integrate-button-font-size">{{
        t("integrate.embed.fontSize")
      }}</label>
      <div class="embed-settings__font-size-control">
        <UISlider
          :value="controls.settings.layout.fontSize"
          :min="8"
          :max="100"
          @update:value="
            controls.setNumericLayoutValue('fontSize', Number($event))
          "
        />
        <UIInputNumber
          id="integrate-button-font-size"
          :model-value="controls.settings.layout.fontSize"
          :min="8"
          :max="100"
          :show-button="false"
          @update:model-value="
            controls.setNumericLayoutValue('fontSize', $event)
          "
        />
      </div>
    </div>
  </section>
</template>

<style scoped>
.embed-settings {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
}
.embed-settings h3 {
  margin: 0;
}
.embed-settings__field-label {
  color: var(--gray-700, #344054);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}
.embed-settings__field-help {
  color: var(--gray-600, #475467);
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}
.embed-settings__types {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 12px;
}
.embed-settings__type {
  width: 100%;
  height: auto !important;
  min-height: 146px;
  padding: 0 !important;
  color: var(--gray-600, #475467);
  font-size: 14px;
  font-weight: 400;
  box-shadow: none !important;
}
.embed-settings__type :deep(.n-button__content) {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 8px;
}
.embed-settings__type :deep(.n-button__border),
.embed-settings__type :deep(.n-button__state-border) {
  display: none;
}
.embed-settings__type--active .embed-settings__type-label {
  color: var(--gray-900, #101828);
  font-weight: 500;
}
.embed-settings__row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.embed-settings__row label {
  min-width: 0;
  flex: 1;
  color: var(--gray-700, #344054);
  font-size: 13px;
}
.embed-settings__position {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
}
.embed-settings__position-options {
  display: flex;
  gap: 8px;
}
.embed-settings__position-button {
  width: 40px;
  min-width: 40px;
  height: 40px !important;
  padding: 0 !important;
  border: 1px solid var(--gray-300, #d0d5dd) !important;
  border-radius: 8px !important;
  background: var(--base-white, #fff) !important;
  color: var(--gray-700, #344054) !important;
}
.embed-settings__position-button :deep(.n-button__border),
.embed-settings__position-button :deep(.n-button__state-border) {
  display: none;
}
.embed-settings__position-button--active {
  border: 2px solid var(--primary-600, #155eef) !important;
  color: var(--primary-600, #155eef) !important;
}
.embed-settings__position-button :deep(.n-button__content) {
  width: 100%;
}
.embed-settings__position-button:first-child :deep(.n-button__content) {
  justify-content: flex-start;
  padding-left: 8px;
}
.embed-settings__position-button:last-child :deep(.n-button__content) {
  justify-content: flex-end;
  padding-right: 8px;
}
.embed-settings__position-glyph {
  display: block;
  width: 12px;
  height: 24px;
  border-radius: 3px;
  background: var(--gray-300, #d0d5dd);
}
.embed-settings__position-button--active .embed-settings__position-glyph {
  background: var(--primary-600, #155eef);
}
.embed-settings__dimensions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
.embed-settings__dimensions > label {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 6px;
  color: var(--gray-700, #344054);
  font-size: 13px;
}
.embed-settings__number-with-unit {
  position: relative;
  display: block;
}
.embed-settings__number-with-unit :deep(.n-input-number-suffix),
.embed-settings__unit {
  color: var(--gray-400, #98a2b3);
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
}
.embed-settings__launcher-copy {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
}
.embed-settings__fields {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.embed-settings__fields > label,
.embed-settings__picker-label,
.embed-settings__two-column label,
.embed-settings__dimensions > label,
.embed-settings__position > span,
.embed-settings__corners legend {
  color: var(--gray-700, #344054);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}
.embed-settings__picker-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.embed-settings__icon-picker :deep(.hl-text-xs-medium) {
  display: none;
}
.embed-settings__two-column,
.embed-settings__two-column {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin: 0;
  padding: 0;
  border: 0;
}
.embed-settings__two-column > div {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.embed-settings__corners legend {
  grid-area: legend;
  margin-bottom: 2px;
  font-size: 14px;
  font-weight: 500;
}
.embed-settings__corners {
  display: grid;
  grid-template-areas:
    "legend legend legend"
    "tl link tr"
    "bl link br";
  grid-template-columns: minmax(0, 1fr) 40px minmax(0, 1fr);
  align-items: center;
  gap: 12px 8px;
  margin: 0;
  padding: 0;
  border: 0;
}
.embed-settings__corner {
  min-width: 0;
}
.embed-settings__corner--tl {
  grid-area: tl;
}
.embed-settings__corner--tr {
  grid-area: tr;
}
.embed-settings__corner--bl {
  grid-area: bl;
}
.embed-settings__corner--br {
  grid-area: br;
}
.embed-settings__corner :deep(.hl-input-number) {
  width: 100%;
}
.embed-settings__corner-glyph {
  display: inline-flex;
  width: 18px;
  height: 18px;
  flex: 0 0 18px;
  align-items: center;
  justify-content: center;
  color: var(--gray-600, #475467);
  font-size: 18px;
  line-height: 1;
}
.embed-settings__corner-glyph::before {
  content: "rounded_corner";
}
.embed-settings__corner-link {
  grid-area: link;
  width: 40px;
  min-width: 40px;
  height: 40px !important;
  align-self: center;
  padding: 0 !important;
  border: 1px solid var(--gray-300, #d0d5dd) !important;
  border-radius: 8px !important;
  color: var(--gray-700, #344054) !important;
  background: var(--base-white, #fff) !important;
}
.embed-settings__font-size-control {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 56px;
  align-items: center;
  gap: 10px;
}
</style>
